/**
 * BasicInfoDrawer — edit drawer for user basic information.
 *
 * Fields: Display Name, Email, Phone, Role (dropdown), Title (dropdown + custom).
 * Handles both cloud (user-manage-2) and on-premise (user-manage) API calls.
 */

import { useState, useEffect, useCallback } from 'react';
import Drawer from '../../../../components/v2/Drawer.jsx';
import { iAmHigherAuthority } from '../../../../libs.jsx';
import { rolesForCampus } from '../../../../utils/palcareRoles.js';

const STANDARD_TITLES = ['Executive Director', 'Maintenance Director', 'Wellness Director', 'Director of Nursing', 'Nurse', 'Caregiver'];

const BasicInfoDrawer = ({ open, onClose, onSave, saving, basicData, roles, campusId, myRoleWeight }) => {
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [roleId, setRoleId] = useState('');
    const [title, setTitle] = useState('');
    const [customTitle, setCustomTitle] = useState('');
    const [showCustomTitle, setShowCustomTitle] = useState(false);

    useEffect(() => {
        if (open && basicData) {
            setDisplayName(basicData.display_name || '');
            setEmail(basicData.email || '');
            setPhone(basicData.phone || '');
            setRoleId(basicData.user_role_id?.toString() || '');
            const t = basicData.title || '';
            if (STANDARD_TITLES.includes(t)) {
                setTitle(t);
                setShowCustomTitle(false);
                setCustomTitle('');
            } else if (t) {
                setTitle('Other');
                setShowCustomTitle(true);
                setCustomTitle(t);
            } else {
                setTitle('');
                setShowCustomTitle(false);
                setCustomTitle('');
            }
        }
    }, [open, basicData]);

    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => document.getElementById('upp-display-name')?.focus(), 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    const handleTitleChange = useCallback((e) => {
        const val = e.target.value;
        setTitle(val);
        if (val === 'Other') {
            setShowCustomTitle(true);
        } else {
            setShowCustomTitle(false);
            setCustomTitle('');
        }
    }, []);

    // Roles you outrank, plus the user's current one so the dropdown can show it. This
    // read `r.role_weight`, which the role rows don't have (they carry user_role_weight),
    // so it let through every role, PalCare ones included. PalCare roles only on a
    // PalCare community (utils/palcareRoles.js).
    const currentRoleId = basicData?.user_role_id;
    const filteredRoles = rolesForCampus(roles, campusId, currentRoleId).filter((r) =>
        String(r.user_role_id) === String(currentRoleId)
        || iAmHigherAuthority(r.user_role_weight, myRoleWeight)
    );

    // Email required for all roles except Caregiver (role_id 6)
    const emailRequired = roleId !== '6';

    const handleSubmit = useCallback(() => {
        const finalTitle = title === 'Other' ? customTitle : title;
        onSave({
            display_name: displayName,
            email: email.toLowerCase(),
            phone: phone.replace(/[\s-]/g, ''),
            user_role_id: parseInt(roleId, 10),
            title: finalTitle,
        });
    }, [displayName, email, phone, roleId, title, customTitle, onSave]);

    const canSubmit = displayName.trim() && (!emailRequired || email.trim()) && roleId;

    return (
        <>
            <Drawer
                open={open}
                onClose={onClose}
                title="Edit User Info"
                size="lg"
                ariaLabel="Edit User Info"
                bodyProps={{ style: { overflowY: 'auto', flex: 1 } }}
                footer={(
                    <>
                        <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>Cancel</button>
                        <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={saving || !canSubmit}>
                            {saving ? 'Saving\u2026' : 'Save Changes'}
                        </button>
                    </>
                )}
            >
                {/* Display Name */}
                <div className="dp-drawer__field">
                    <label htmlFor="upp-display-name" className="dp-drawer__label">
                        Display Name <span className="dp-drawer__required">*</span>
                    </label>
                    <input id="upp-display-name" className="dp-drawer__input" value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)} pattern="[a-zA-Z0-9 ]+" required />
                </div>

                {/* Email */}
                <div className="dp-drawer__field">
                    <label htmlFor="upp-email" className="dp-drawer__label">
                        Email {emailRequired ? <span className="dp-drawer__required">*</span> : null}
                    </label>
                    <input id="upp-email" type="email" className="dp-drawer__input" value={email}
                        onChange={(e) => setEmail(e.target.value)} required={emailRequired} />
                </div>

                {/* Phone */}
                <div className="dp-drawer__field">
                    <label htmlFor="upp-phone" className="dp-drawer__label">Phone</label>
                    <input id="upp-phone" type="tel" className="dp-drawer__input" value={phone}
                        onChange={(e) => setPhone(e.target.value)} pattern="[0-9\-]{10,12}" placeholder="000-000-0000" />
                </div>

                {/* Role */}
                <div className="dp-drawer__field">
                    <label htmlFor="upp-role" className="dp-drawer__label">
                        Role <span className="dp-drawer__required">*</span>
                    </label>
                    <select id="upp-role" className="dp-drawer__input" value={roleId}
                        onChange={(e) => setRoleId(e.target.value)} required>
                        <option value="">Select role...</option>
                        {filteredRoles.map((r) => (
                            <option key={r.user_role_id} value={r.user_role_id}>{r.user_role_name}</option>
                        ))}
                    </select>
                </div>

                {/* Title */}
                <div className="dp-drawer__field">
                    <label htmlFor="upp-title" className="dp-drawer__label">Job Title</label>
                    <div className="upp-title-row">
                        <select id="upp-title" className="dp-drawer__input" value={title} onChange={handleTitleChange}>
                            <option value="">Select title...</option>
                            {STANDARD_TITLES.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                            <option value="Other">Other (custom)</option>
                        </select>
                    </div>
                    {showCustomTitle ? (
                        <input className="dp-drawer__input" style={{ marginTop: 'var(--space-2)' }} value={customTitle}
                            onChange={(e) => setCustomTitle(e.target.value)} placeholder="Enter custom title..." />
                    ) : null}
                </div>
            </Drawer>
        </>
    );
};

export default BasicInfoDrawer;
